import React, { useEffect, useState } from "react";
import { Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Field definitions per document type — these become the `data` JSON on the certificate.
const SCHEMAS = {
  medical: { label: "Medical certificate", fields: [
    ["examined_on", "Examined on", "date"], ["diagnosis", "Diagnosis"], ["findings", "Findings", "area"],
    ["advice", "Advice", "area"], ["fitness", "Fitness statement"], ["purpose", "Purpose"]] },
  sick_leave: { label: "Sick leave certificate", fields: [
    ["diagnosis", "Diagnosis"], ["from_date", "Leave from", "date"], ["to_date", "Leave to", "date"],
    ["days", "Number of days"], ["resume_date", "May resume duty on", "date"]] },
  referral: { label: "Referral letter", fields: [
    ["refer_to", "Refer to (doctor)"], ["speciality", "Speciality"], ["diagnosis", "Provisional diagnosis"],
    ["summary", "Clinical summary", "area"], ["investigations", "Investigations done", "area"],
    ["treatment", "Treatment given", "area"], ["reason", "Reason for referral", "area"]] },
  consent: { label: "Consent form", fields: [
    ["procedure", "Procedure / treatment"], ["signatory", "Signed by"], ["relation", "Relation to patient"],
    ["anaesthesia", "Anaesthesia"], ["risks", "Risks explained", "area"], ["notes", "Additional notes", "area"]] },
};

export default function Certificates() {
  const { fq, user } = useApp();
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("medical");
  const [patientId, setPatientId] = useState("");
  const [data, setData] = useState({});

  const load = () => api.get(`/certificates${fq ? `?${fq}` : ""}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fq]);
  useEffect(() => { api.get("/patients").then(({ data }) => setPatients(data)); }, []);

  const save = async () => {
    if (!patientId) return toast.error("Select a patient");
    try {
      const { data: created } = await api.post("/certificates", { type, patient_id: Number(patientId), data });
      toast.success(`${SCHEMAS[type].label} ${created.number} issued`);
      setOpen(false); setData({}); setPatientId(""); load();
      openPdf(`/certificates/${created.id}/pdf`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canIssue = ["super_admin", "doctor"].includes(user.role);

  return (
    <div className="space-y-5" data-testid="certificates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">Documents & forms</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Certificates</h1>
        </div>
        {canIssue && (
          <Button className="gap-2" onClick={() => setOpen(true)} data-testid="issue-certificate-button">
            <Plus className="h-4 w-4" /> Issue document
          </Button>
        )}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="certificates-table">
            <TableHeader><TableRow>
              <TableHead>Number</TableHead><TableHead>Type</TableHead><TableHead>Patient</TableHead>
              <TableHead>Issued by</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Document</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                  No documents issued yet.
                </TableCell></TableRow>
              )}
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`certificate-row-${c.id}`} className="hover:bg-muted/50">
                  <TableCell className="font-mono text-xs">{c.number}</TableCell>
                  <TableCell><Badge variant="outline">{SCHEMAS[c.type]?.label || c.type}</Badge></TableCell>
                  <TableCell className="font-medium">{c.patient_name}
                    <span className="block text-xs font-mono text-muted-foreground">{c.patient_uhid}</span>
                  </TableCell>
                  <TableCell>{c.doctor_name}</TableCell>
                  <TableCell className="text-xs font-mono">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid={`certificate-pdf-${c.id}`}
                      onClick={() => openPdf(`/certificates/${c.id}/pdf`)}>
                      <FileText className="h-3.5 w-3.5" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="certificate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Issue document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Document type</Label>
                <Select value={type} onValueChange={(v) => { setType(v); setData({}); }}>
                  <SelectTrigger data-testid="certificate-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SCHEMAS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Patient</Label>
                <Select value={patientId} onValueChange={setPatientId}>
                  <SelectTrigger data-testid="certificate-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} · {p.uhid}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {SCHEMAS[type].fields.map(([k, label, kind]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  {kind === "area" ? (
                    <Textarea rows={2} value={data[k] || ""} data-testid={`certificate-${k}-input`}
                      onChange={(e) => setData({ ...data, [k]: e.target.value })} />
                  ) : (
                    <Input type={kind === "date" ? "date" : "text"} value={data[k] || ""}
                      data-testid={`certificate-${k}-input`}
                      onChange={(e) => setData({ ...data, [k]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-certificate-button">Issue & open PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
